import { useCallback, useSyncExternalStore } from 'react';
import type { Editor } from '@tiptap/core';
import {
  Bold,
  Italic,
  Strikethrough,
  Underline,
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Link2,
  Link2Off,
} from 'lucide-react';
import { Toggle } from '@/components/ui/toggle';
import { Separator } from '@/components/ui/separator';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

interface EmailToolbarProps {
  editor: Editor | null;
  disabled?: boolean;
  className?: string;
}

/**
 * Подписка на изменения редактора без лишних ререндеров всего дерева.
 * Тулбар должен подсвечивать активные форматирования (bold/heading/...).
 */
function useEditorState(editor: Editor | null): number {
  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      if (!editor) {
        return () => {
          // редактор ещё не готов — отписываться не от чего
        };
      }
      editor.on('transaction', onStoreChange);
      editor.on('selectionUpdate', onStoreChange);
      return () => {
        editor.off('transaction', onStoreChange);
        editor.off('selectionUpdate', onStoreChange);
      };
    },
    [editor],
  );

  const getSnapshot = useCallback(
    () => (editor ? editor.state.selection.from * 1_000 + editor.state.selection.to : 0),
    [editor],
  );

  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

interface ToolbarToggleProps {
  label: string;
  pressed: boolean;
  disabled?: boolean;
  onPressedChange: () => void;
  children: React.ReactNode;
}

function ToolbarToggle({
  label,
  pressed,
  disabled,
  onPressedChange,
  children,
}: ToolbarToggleProps) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Toggle
          size="sm"
          aria-label={label}
          pressed={pressed}
          disabled={disabled}
          onPressedChange={onPressedChange}
        >
          {children}
        </Toggle>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}

export function EmailToolbar({ editor, disabled, className }: EmailToolbarProps) {
  // Подписка обеспечивает ререндер тулбара при смене выделения/форматирования.
  useEditorState(editor);

  if (!editor) {
    return (
      <div
        className={cn(
          'flex h-11 items-center gap-1 border-b bg-muted/40 px-2',
          className,
        )}
      />
    );
  }

  const isDisabled = disabled || !editor.isEditable;

  const handleLink = () => {
    if (editor.isActive('link')) {
      editor.chain().focus().unsetLink().run();
      return;
    }
    const previous = editor.getAttributes('link').href;
    const href = window.prompt('URL ссылки', typeof previous === 'string' ? previous : 'https://');
    if (href === null) {
      return;
    }
    if (href.trim() === '') {
      editor.chain().focus().unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: href.trim() }).run();
  };

  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-1 border-b bg-muted/40 px-2 py-1.5',
        className,
      )}
    >
      <ToolbarToggle
        label="Жирный"
        pressed={editor.isActive('bold')}
        disabled={isDisabled}
        onPressedChange={() => editor.chain().focus().toggleBold().run()}
      >
        <Bold className="size-4" />
      </ToolbarToggle>
      <ToolbarToggle
        label="Курсив"
        pressed={editor.isActive('italic')}
        disabled={isDisabled}
        onPressedChange={() => editor.chain().focus().toggleItalic().run()}
      >
        <Italic className="size-4" />
      </ToolbarToggle>
      <ToolbarToggle
        label="Подчёркнутый"
        pressed={editor.isActive('underline')}
        disabled={isDisabled}
        onPressedChange={() => editor.chain().focus().toggleUnderline().run()}
      >
        <Underline className="size-4" />
      </ToolbarToggle>
      <ToolbarToggle
        label="Зачёркнутый"
        pressed={editor.isActive('strike')}
        disabled={isDisabled}
        onPressedChange={() => editor.chain().focus().toggleStrike().run()}
      >
        <Strikethrough className="size-4" />
      </ToolbarToggle>

      <Separator orientation="vertical" className="mx-1 h-6" />

      <ToolbarToggle
        label="Заголовок 1"
        pressed={editor.isActive('heading', { level: 1 })}
        disabled={isDisabled}
        onPressedChange={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
      >
        <Heading1 className="size-4" />
      </ToolbarToggle>
      <ToolbarToggle
        label="Заголовок 2"
        pressed={editor.isActive('heading', { level: 2 })}
        disabled={isDisabled}
        onPressedChange={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
      >
        <Heading2 className="size-4" />
      </ToolbarToggle>
      <ToolbarToggle
        label="Заголовок 3"
        pressed={editor.isActive('heading', { level: 3 })}
        disabled={isDisabled}
        onPressedChange={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
      >
        <Heading3 className="size-4" />
      </ToolbarToggle>

      <Separator orientation="vertical" className="mx-1 h-6" />

      <ToolbarToggle
        label="Маркированный список"
        pressed={editor.isActive('bulletList')}
        disabled={isDisabled}
        onPressedChange={() => editor.chain().focus().toggleBulletList().run()}
      >
        <List className="size-4" />
      </ToolbarToggle>
      <ToolbarToggle
        label="Нумерованный список"
        pressed={editor.isActive('orderedList')}
        disabled={isDisabled}
        onPressedChange={() => editor.chain().focus().toggleOrderedList().run()}
      >
        <ListOrdered className="size-4" />
      </ToolbarToggle>

      <Separator orientation="vertical" className="mx-1 h-6" />

      <ToolbarToggle
        label={editor.isActive('link') ? 'Убрать ссылку' : 'Вставить ссылку'}
        pressed={editor.isActive('link')}
        disabled={isDisabled}
        onPressedChange={handleLink}
      >
        {editor.isActive('link') ? (
          <Link2Off className="size-4" />
        ) : (
          <Link2 className="size-4" />
        )}
      </ToolbarToggle>

      <span className="ml-auto hidden text-xs text-muted-foreground sm:inline">
        Наберите «/» для кнопок, секций и колонок
      </span>
    </div>
  );
}
